/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { countryCodes } from '@/constants/locale';
import { usePageType } from '@/features/category/api/getPageType';
import { useLocale } from '@/hooks/useLocale';
import { useIsBot } from '@/utils/useIsBot';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useEffect, useRef, useState } from 'react';
import root from 'window-or-global';
import { useTopReviews } from '../../api/getTopReviews';
import { type MakeModelYearPageType } from '../../utils/getShopByType';
import { BazaarVoiceShelfSection } from './BazaarVoiceShelfSection';

type BazaarVoiceShelfProps = {
  productId: string | null | undefined;
  makeModelYearPageType: MakeModelYearPageType;
};

const BazaarVoiceShelfComponent = ({ productId, makeModelYearPageType }: BazaarVoiceShelfProps) => {
  const router = useLegacyRouter();
  const isABot = useIsBot();

  const scrolled = useRef(false);

  const [topReviewsQueryEnabled, setTopReviewsQueryEnabled] = useState(false);

  const locale = useLocale();

  const { data: pageTypeData } = usePageType();

  const { data: reviews } = useTopReviews({
    enabled: topReviewsQueryEnabled,
    partTypeId: productId ?? '',
    pageType: makeModelYearPageType,
    vehicleMake: pageTypeData?.make ?? undefined,
    vehicleModel: pageTypeData?.model ?? undefined,
    vehicleYear: pageTypeData?.year ?? undefined,
    minimumReviewRating: undefined,
  });

  useEffect(() => {
    const fetchReviewsOnScroll = () => {
      if (!scrolled.current && productId) {
        scrolled.current = true;
        setTopReviewsQueryEnabled(true);
      } else {
        root.removeEventListener('scroll', fetchReviewsOnScroll);
      }
    };

    if (
      (makeModelYearPageType === 'ShopByMake' ||
        makeModelYearPageType === 'ShopByModel' ||
        makeModelYearPageType === 'ShopByYear') &&
      !isABot &&
      locale !== countryCodes.mx
    ) {
      root.addEventListener('scroll', fetchReviewsOnScroll);
    }

    return () => {
      scrolled.current = false;
      root.removeEventListener('scroll', fetchReviewsOnScroll);
    };
  }, [
    router.asPath,
    pageTypeData?.make,
    pageTypeData?.model,
    pageTypeData?.year,
    makeModelYearPageType,
    locale,
    productId,
    isABot,
  ]);

  if (!reviews?.length) {
    return null;
  }

  return <BazaarVoiceShelfSection reviews={reviews} />;
};

export const BazaarVoiceShelf = BazaarVoiceShelfComponent;
